import { useEffect, useState } from "react";
import { Wallet, History, Receipt, Paperclip } from "lucide-react";
import { api, apiError, descargarPdf } from "@/lib/api";
import { money, fecha } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const METODOS = ["efectivo", "transferencia", "tarjeta", "cheque"];

/** Cobro total o parcial de una factura, con el historial de abonos ya registrados. */
export const CobroFactura = ({ factura, onClose, onCobrado }) => {
  const [monto, setMonto] = useState("");
  const [metodo, setMetodo] = useState("efectivo");
  const [referencia, setReferencia] = useState("");
  const [nota, setNota] = useState("");
  const [enviarRecibo, setEnviarRecibo] = useState(false);
  const [comprobante, setComprobante] = useState({ nombre: "", base64: "" });
  const [historial, setHistorial] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const tomarComprobante = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      return toast.error("El archivo supera los 8 MB; use una foto o captura más liviana");
    }
    const reader = new FileReader();
    reader.onload = () => setComprobante({ nombre: file.name, base64: String(reader.result) });
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!factura) return;
    setReferencia("");
    setNota("");
    setMetodo("efectivo");
    setEnviarRecibo(false);
    setComprobante({ nombre: "", base64: "" });
    setHistorial(null);
    api.get(`/facturas/${factura.id}/pagos`).then((r) => {
      setHistorial(r.data);
      setMonto(String(r.data.balance));
    }).catch(() => setMonto(String(factura.saldo ?? factura.monto_total)));
  }, [factura]);

  const balance = historial?.balance ?? factura?.saldo ?? factura?.monto_total ?? 0;
  const restante = Math.max(balance - (Number(monto) || 0), 0);

  const recibo = (pid, numero) =>
    descargarPdf(`/pagos/${pid}/recibo`, {}, `recibo-${numero || pid}.pdf`)
      .catch((e) => toast.error(apiError(e, "No se pudo generar el recibo")));

  const verComprobante = async (pid) => {
    try {
      const { data } = await api.get(`/pagos/${pid}/comprobante`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 8000);
    } catch (e) {
      toast.error(apiError(e, "No se pudo abrir el comprobante"));
    }
  };

  const cobrar = async () => {
    const valor = Number(monto);
    if (!valor || valor <= 0) return toast.error("Indique el monto que está recibiendo");
    if (valor > balance + 0.01) return toast.error(`El monto supera el balance de ${money(balance)}`);
    setGuardando(true);
    try {
      const { data } = await api.post(`/facturas/${factura.id}/abono`, {
        monto: valor, metodo, referencia: referencia.trim() || null, nota: nota.trim() || null,
        enviar_recibo: enviarRecibo,
        comprobante_nombre: comprobante.base64 ? comprobante.nombre : null,
        comprobante_base64: comprobante.base64 || null,
      });
      const correo = data.correo?.error
        ? ` El recibo no se pudo enviar: ${data.correo.error}`
        : data.correo?.enviado_a ? ` Recibo enviado a ${data.correo.enviado_a}.` : "";
      toast.success(data.liquidada ? `Factura ${factura.ncf} cobrada por completo`
                                   : `Abono de ${money(valor)} registrado`,
        { description: `Recibo ${data.recibo}.`
            + (data.liquidada ? "" : ` Balance restante ${money(data.balance)}.`) + correo });
      await recibo(data.pago_id, data.recibo);
      onCobrado?.(data);
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={!!factura} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-cobro">
        <DialogHeader>
          <DialogTitle className="font-display">Cobrar factura {factura?.ncf}</DialogTitle>
          <DialogDescription>
            {factura?.estudiante_nombre} · {factura?.concepto || factura?.detalle?.[0]?.concepto}
          </DialogDescription>
        </DialogHeader>
        {factura && (
          <div className="grid gap-4">
            <div className="grid grid-cols-3 gap-3 rounded-md border border-border bg-muted/30 p-4 text-center">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Total</p>
                <p className="font-display text-base font-bold">{money(factura.monto_factura ?? factura.monto_total)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Abonado</p>
                <p className="font-display text-base font-bold">{money(historial?.monto_pagado ?? factura.monto_pagado ?? 0)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Balance</p>
                <p className="font-display text-base font-bold text-destructive" data-testid="cobro-balance">
                  {money(balance)}
                </p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Monto que recibe (RD$)</Label>
                <Input type="number" value={monto} data-testid="cobro-monto"
                       onChange={(e) => setMonto(e.target.value)} />
                <p className="text-xs text-muted-foreground">
                  {restante > 0 ? `Quedaría un balance de ${money(restante)}` : "Liquida la factura completa"}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Método</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={metodo} data-testid="cobro-metodo"
                        onChange={(e) => setMetodo(e.target.value)}>
                  {METODOS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Referencia (opcional)</Label>
                <Input value={referencia} data-testid="cobro-referencia" placeholder="No. de transferencia o cheque"
                       onChange={(e) => setReferencia(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Nota interna (opcional)</Label>
                <Input value={nota} data-testid="cobro-nota"
                       onChange={(e) => setNota(e.target.value)} />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {[0.25, 0.5, 1].map((p) => (
                <Button key={p} size="sm" variant="outline" className="rounded-full"
                        data-testid={`cobro-atajo-${p * 100}`}
                        onClick={() => setMonto(String(Math.round(balance * p * 100) / 100))}>
                  {p === 1 ? "Todo el balance" : `${p * 100}%`}
                </Button>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Comprobante o captura del pago (opcional)</Label>
              <Input type="file" accept="image/*,application/pdf" onChange={tomarComprobante}
                     data-testid="cobro-comprobante" />
              <p className="text-xs text-muted-foreground">
                {comprobante.nombre
                  ? `Adjunto: ${comprobante.nombre}`
                  : "Foto del depósito, captura de la transferencia o PDF del banco (máx. 8 MB)."}
              </p>
            </div>

            <label className="flex items-center gap-3 rounded-md border border-border bg-muted/20 p-3 text-sm">
              <Checkbox checked={enviarRecibo} data-testid="cobro-enviar-recibo"
                        onCheckedChange={(v) => setEnviarRecibo(!!v)} />
              Enviarle el recibo por correo al tutor (el PDF se descarga siempre)
            </label>

            {historial?.pagos?.length > 0 && (
              <div className="rounded-md border border-border/60 p-4" data-testid="cobro-historial">
                <p className="flex items-center gap-2 text-sm font-semibold">
                  <History className="h-4 w-4 text-primary" /> Abonos registrados
                </p>
                <ul className="mt-3 space-y-2 text-xs">
                  {historial.pagos.map((p, i) => (
                    <li key={i} className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">
                        {fecha(p.fecha || p.creado_en)} · {p.metodo}
                        {p.recibo ? ` · ${p.recibo}` : ""}
                        {p.registrado_por ? ` · ${p.registrado_por}` : ""}
                      </span>
                      <span className="flex items-center gap-2 font-mono font-semibold">
                        {money(p.monto)}
                        <Badge variant="outline">{p.estado === "pagado" ? (p.tipo || "abono") : p.estado}</Badge>
                        {p.tiene_comprobante && (
                          <Button size="sm" variant="ghost" className="h-7 px-2"
                                  data-testid={`ver-comprobante-pago-${p.id}`}
                                  title="Ver el comprobante adjunto"
                                  onClick={() => verComprobante(p.id)}>
                            <Paperclip className="h-3 w-3" />
                          </Button>
                        )}
                        {p.id && (
                          <Button size="sm" variant="ghost" className="h-7 px-2"
                                  data-testid={`recibo-${p.id}`} title="Descargar el recibo"
                                  onClick={() => recibo(p.id, p.recibo)}>
                            <Receipt className="h-3 w-3" />
                          </Button>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button className="rounded-full py-6" onClick={cobrar} disabled={guardando}
                    data-testid="confirmar-cobro">
              <Wallet className="mr-2 h-4 w-4" />
              {guardando ? "Registrando…" : `Registrar cobro de ${money(Number(monto) || 0)}`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
